import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Modal, type ModalProps, Space } from 'antd';
import cx from 'classnames';
import { isFunction, isNull } from 'lodash-es';
import React from 'react';
import { ActionButton } from '..';

const typeIcons = {
  success: <CheckCircleOutlined className="text-[#FF7D00] text-[32px] mb-4" />,
  info: <ExclamationCircleOutlined />,
  error: <CloseCircleOutlined className="text-[#FF7D00] text-[32px] mb-4" />,
};

type IconType = keyof typeof typeIcons;
export interface BaseModalProps extends ModalProps {
  close?: (...args: any[]) => void;
  content?: React.ReactNode;
  icon?: IconType;
  triggerCancel?: boolean;
  actions?: {
    showOkBtn?: boolean;
    showCancelBtn?: boolean;
    wrapperClassName?: string;
    render?: (props: BaseModalProps) => React.ReactNode;
  } | null;
}

const BaseModal: React.FC<BaseModalProps> = (props) => {
  const {
    open,
    close,
    width = 420,
    icon,
    title,
    content,
    triggerCancel = false,
    actions = {},
    okText,
    okButtonProps,
    onOk,
    cancelText,
    cancelButtonProps,
    onCancel,
    ...others
  } = props;

  const getIconContent = () => {
    if (typeof icon === 'string') {
      return (
        <div className="text-center text-[#FF7D00] text-[32px] mb-4">
          {typeIcons[icon]}
        </div>
      );
    }
    return icon;
  };

  const getActions = () => {
    if (isNull(actions)) return null;
    const {
      showOkBtn = true,
      showCancelBtn = false,
      wrapperClassName,
      render,
    } = actions;
    if (isFunction(render)) {
      return render(props);
    }
    const _actions = [];
    if (showOkBtn) {
      _actions.push(
        <ActionButton
          key="ok"
          actionFn={onOk}
          close={close}
          buttonProps={{
            className: cx(
              showCancelBtn ? 'w-[150px]' : 'w-[254px]',
              'rounded-[4px]',
            ),
            size: 'large',
            type: 'primary',
            ...okButtonProps,
          }}
        >
          {okText || '确定'}
        </ActionButton>,
      );
    }
    if (showCancelBtn) {
      _actions.push(
        <ActionButton
          key="cancel"
          close={close}
          buttonProps={{
            className: cx(
              showOkBtn ? 'w-[150px]' : 'w-[254px]',
              'rounded-[4px]',
            ),
            size: 'large',
            ...cancelButtonProps,
          }}
          actionFn={onCancel}
        >
          {cancelText || '取消'}
        </ActionButton>,
      );
    }

    return (
      <Space
        className={cx('pt-8 flex justify-center', wrapperClassName)}
        size={16}
      >
        {_actions.map((item) => item)}
      </Space>
    );
  };

  return (
    <Modal
      open={open}
      width={width}
      destroyOnClose
      footer={null}
      centered
      onCancel={() => close?.({ triggerCancel })}
      {...others}
    >
      <div className="leading-none py-2">
        {getIconContent()}
        {title && (
          <div className="text-18 text-cs1 font-medium text-center">
            {title}
          </div>
        )}
        {content}
        {getActions()}
      </div>
    </Modal>
  );
};
export default BaseModal;
